"use client"

import { useAtomValue } from "jotai"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"
import { authClient } from "@/lib/auth/auth-client"
import { privacyConsentAtom } from "@/lib/privacy/atom"
import { getCurrentAreaInClient } from "@/lib/utils.client"
import { initDataLayer, pageLoad } from "./gtm"
import { trackService } from "./track-service"

export function GlobalTracker() {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const hasTrackedPageLoad = useRef<boolean>(false)

	// 获取隐私同意状态
	const privacyConsent = useAtomValue(privacyConsentAtom)

	// 获取用户会话信息
	const { data: session } = authClient.useSession()

	// 初始化数据层
	useEffect(() => {
		initDataLayer()
	}, [])

	// GTM 页面加载事件追踪 + 业务埋点 WebPageView
	useEffect(() => {
		// 在应用初始化完成后触发，无需等待隐私同意
		if (!hasTrackedPageLoad.current) {
			const currentArea = getCurrentAreaInClient()
			const userId = session?.user?.id

			// 推送页面加载事件
			pageLoad({
				pop_user_id: userId || undefined, // 只在有用户ID时推送
				pop_country_id: currentArea.toLowerCase(), // 转换为小写格式
				gcs_analytics_storage: privacyConsent, // 基于隐私同意状态
				gcs_ad_storage: privacyConsent, // 基于隐私同意状态
				gcs_ad_user_data: privacyConsent, // 基于隐私同意状态
				gcs_ad_personalization: privacyConsent, // 基于隐私同意状态
			})

			// 同时推送业务埋点 WebPageView
			trackService.trackPageView(
				{
					pathname: pathname,
					search: searchParams.toString(),
					timestamp: Date.now(),
				},
				session,
			)

			hasTrackedPageLoad.current = true
		}
	}, [session?.user?.id, privacyConsent, pathname, searchParams])

	// 全局点击埋点
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement

			// 过滤掉一些不需要埋点的元素
			if (
				target.tagName === "SCRIPT" ||
				target.tagName === "STYLE" ||
				target.tagName === "META" ||
				target.tagName === "LINK" ||
				target.closest("[data-no-track]")
			) {
				return
			}

			// 获取点击元素的信息
			const elementInfo = {
				tagName: target.tagName.toLowerCase(),
				className: target.className || "",
				id: target.id || "",
				text: target.textContent?.trim().substring(0, 100) || "",
				href: (target as HTMLAnchorElement).href || "",
				type: (target as HTMLInputElement).type || "",
				value: (target as HTMLInputElement).value || "",
			}

			// 点击事件使用批量上报，不阻塞用户交互
			trackService.trackClick(
				target,
				{
					elementInfo,
					timestamp: Date.now(),
					x: event.clientX,
					y: event.clientY,
				},
				session,
			)
		}

		// 页面离开埋点 - 在新页面加载时触发
		const handlePageLeave = () => {
			// 获取上一个页面的信息
			const referrer = document.referrer
			if (referrer) {
				trackService.trackPageLeave(
					{
						timestamp: Date.now(),
						referrer_url: referrer,
						// 计算页面停留时长（从页面加载到离开）
						duration: Date.now() - performance.timing.navigationStart,
					},
					session,
				)
			}
		}

		// 在新页面加载时触发页面离开埋点
		handlePageLeave()

		// 添加事件监听器
		document.addEventListener("click", handleClick, true)

		// 清理事件监听器
		return () => {
			document.removeEventListener("click", handleClick, true)
		}
	}, [])

	return null // 这个组件不渲染任何内容
}
